import React, { useState, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import {
  AIGeneratorFullScreenProps,
  ATSProvider,
  JobDescriptionEntity,
  TemplateData,
} from "../types/Step0.types";
import { JobPostFormData } from "../schemas/jobPostValidation";
import { useAIGeneration } from "./hooks/useAIGeneration";
import { useTemplateSelection } from "./hooks/useTemplateSelection";
import { useSubscriptionLimit } from "../hooks/useSubscriptionLimit";
import PromptEditor from "../components/PromptEditor";
import QuickStartTemplates from "./components/QuickStartTemplates";
import ATSIntegration from "./components/ATSIntegration";
import DraftManagementModal from "./components/DraftManagementModal";
import { useDraftDetection, DraftInfo } from "../hooks/useDraftDetection";
import { Typography, message, Tag, Button, Flex } from "antd";
import { ExclamationCircleOutlined, FileTextOutlined } from "@ant-design/icons";
import SubscriptionWarning from "../../../Components/SubscriptionWarning";
import "./index.scss";

const AIGeneratorFullScreen: React.FC<AIGeneratorFullScreenProps> = ({
  onGenerate,
  isVisible = true,
  onRestoreDraft,
  onATSImport,
}) => {
  const drafts = useDraftDetection();
  const [isDraftModalVisible, setIsDraftModalVisible] = useState(false);
  const form = useFormContext<JobPostFormData>();

  // Subscription limit checking
  const {
    checkLimit,
    isLimitReached,
    isApproachingLimit,
    remaining,
    hasValidData,
  } = useSubscriptionLimit();

  // Local state
  const [prompt, setPrompt] = useState<string>("");
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);

  // Custom hooks
  const { generateJobPost, isGenerating, error, clearError } = useAIGeneration({
    onSuccess: (jobPost: JobDescriptionEntity) => {
      // Clear prompt after successful generation
      setPrompt("");
      // Call parent callback
      onGenerate(jobPost);
    },
    onError: (errorMessage: string) => {
      console.error("AI Generation Error:", errorMessage);
    },
  });

  const { selectTemplate } = useTemplateSelection();

  /**
   * Handle prompt change
   * Updates local prompt state
   */
  const handlePromptChange = useCallback(
    (value: string) => {
      setPrompt(value);
      // Clear any existing errors when user starts typing
      if (error) {
        clearError();
      }
    },
    [error, clearError],
  );

  /**
   * Handle AI generation
   * Triggers AI generation with current prompt
   * Checks subscription limit before generation
   */
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || isGenerating) {
      return;
    }

    // Check subscription limit before generation
    const limitCheck = checkLimit("new"); // Default to 'new' mode

    if (limitCheck.shouldBlock) {
      // Show upgrade popup
      setShowUpgradePopup(true);
      if (limitCheck.reason) {
        message.warning(limitCheck.reason, 4);
      }
      return;
    }

    // If limit check has warning but allows, show info message
    if (limitCheck.reason && !limitCheck.shouldBlock) {
      message.info(limitCheck.reason, 3);
    }

    try {
      await generateJobPost(prompt);
    } catch (err) {
      console.error("Generation failed:", err);
    }
  }, [prompt, isGenerating, generateJobPost, checkLimit]);

  /**
   * Handle template selection
   * Pre-fills prompt with template description
   */
  const handleTemplateSelect = useCallback(
    (template: TemplateData) => {
      const templateDescription = selectTemplate(template);
      setPrompt(templateDescription);
      // Clear any existing errors
      if (error) {
        clearError();
      }
    },
    [selectTemplate, error, clearError],
  );

  // TODO: ATS Integration - Commented out for future release
  /**
   * Handle ATS click (future functionality)
   * Placeholder for ATS integration
   */
  const handleATSClick = useCallback((provider: ATSProvider) => {
    console.log("ATS Provider clicked:", provider);
    // Future: Implement ATS integration
  }, []);

  /**
   * Handle ATS import
   * Prefills prompt editor with job post information and stores ATS metadata in form state
   */
  const handleATSImport = useCallback(
    (promptText: string, appJobId: string, appType: string) => {
      setPrompt(promptText);

      // Store ATS info in form state (will be persisted in drafts automatically)
      const currentStep0 = form.getValues("step0") || { prompt: "" };
      const updatedStep0 = {
        ...currentStep0,
        prompt: promptText,
        appJobId,
        appType,
      };
      form.setValue("step0", updatedStep0);

      // Verify the values were set
      const step0AfterSet = form.getValues("step0");
      console.log("[Step0AIGenerator] Stored ATS info in form:", {
        appJobId,
        appType,
      });
      console.log(
        "[Step0AIGenerator] Step0 after setting ATS info:",
        step0AfterSet,
      );

      // Clear any existing errors
      if (error) {
        clearError();
      }
    },
    [error, clearError, form],
  );

  const handleRestore = useCallback(
    (draft: DraftInfo) => {
      if (onRestoreDraft) {
        onRestoreDraft({
          storageKey: draft.storageKey,
          mode: draft.mode,
          jobPostId: draft.jobPostId,
          step: draft.step,
        });
      }
    },
    [onRestoreDraft],
  );

  const handleDelete = useCallback((draft: DraftInfo) => {
    try {
      localStorage.removeItem(draft.storageKey);
      // Force re-render by triggering storage event
      globalThis.dispatchEvent(new Event("storage"));
    } catch (error) {
      console.error("Error deleting draft:", error);
      message.error("Unable to delete the draft. Please try again");
    }
  }, []);

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className="ai-generator-fullscreen">
      <div className="generator-content">
        {/* Prompt Editor */}
        <div className="prompt-section">
          <PromptEditor
            prompt={prompt}
            onPromptChange={handlePromptChange}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            disabled={isLimitReached && hasValidData}
          />
        </div>

        {/* Draft Restoration */}
        <div className="ats-section">
          <Flex gap={12} align="center" wrap="wrap">
            {/* TODO: ATS Integration - Commented out for future release */}
            <ATSIntegration
              onATSClick={handleATSClick}
              onImport={handleATSImport}
            />
            {drafts.length > 0 && (
              <Button
                type="text"
                size="middle"
                icon={<FileTextOutlined />}
                onClick={() => setIsDraftModalVisible(true)}
                className="continue-draft-button"
                style={{
                  height: "auto",
                  padding: "4px 8px",
                  fontWeight: 400,
                  border: "1px solid var(--ant-color-border, #d9d9d9)",
                  borderRadius: "4px",
                  background: "transparent",
                  color: "var(--ant-color-text, #262626)",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 14,
                }}
              >
                Continue from Draft {drafts.length > 1 && `(${drafts.length})`}
              </Button>
            )}
          </Flex>
        </div>

        {/* Quick Start Templates */}
        <div className="templates-section">
          <QuickStartTemplates onTemplateSelect={handleTemplateSelect} />
        </div>

        {/* Subscription Limit Warning */}
        {isApproachingLimit && hasValidData && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <Tag color="orange" icon={<ExclamationCircleOutlined />}>
              Approaching limit: {remaining} job post
              {remaining === 1 ? "" : "s"} remaining
            </Tag>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-section">
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
              <button
                className="error-clear"
                onClick={clearError}
                aria-label="Clear error"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isGenerating && (
          <div className="loading-overlay">
            <div className="loading-content">
              <div className="loading-spinner"></div>
              <Typography.Text className="loading-text">
                Generating your job post...
              </Typography.Text>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Popup */}
      <SubscriptionWarning
        open={showUpgradePopup}
        handleClose={() => setShowUpgradePopup(false)}
        title="Limit Reached"
        type="job-post-limit"
      />

      {/* Draft Management Modal */}
      <DraftManagementModal
        visible={isDraftModalVisible}
        onClose={() => setIsDraftModalVisible(false)}
        drafts={drafts}
        onRestore={handleRestore}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default AIGeneratorFullScreen;
